import React, { useCallback, useEffect, useState } from "react";
import { Send, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

export default function InboxPage() {
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [channel, setChannel] = useState("email");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => api.get("/inbox").then(r => setMessages(r.data)), []);
  useEffect(() => { load(); api.get("/contacts").then(r => setContacts(r.data)); }, [load]);

  const send = async () => {
    if (!contactId || !body) return toast.error("Pick a contact and enter a message");
    setSending(true);
    try {
      await api.post("/inbox/send", { contact_id: contactId, channel, body, subject });
      toast.success("Sent");
      setBody(""); setSubject("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const contactName = (id) => contacts.find(c => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Inbox</h1>
          <p className="text-slate-600 mt-1">Unified log of inbound and outbound SMS + email.</p>
        </div>
        <button className="owc-btn-outline text-sm inline-flex items-center gap-2" onClick={load}><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="owc-card p-5">
          <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] mb-4 flex items-center gap-2"><Send className="h-4 w-4 text-[color:var(--owc-sky)]" /> Compose</h2>
          <div className="space-y-3">
            <select data-testid={T.inbox.contactSelect} className="owc-input" value={contactId} onChange={e => setContactId(e.target.value)}>
              <option value="">Choose contact…</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.name} — {c.email || c.phone}</option>)}
            </select>
            <select data-testid={T.inbox.channel} className="owc-input" value={channel} onChange={e => setChannel(e.target.value)}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
            {channel === "email" && (
              <input className="owc-input" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
            )}
            <textarea data-testid={T.inbox.body} className="owc-input min-h-[120px]" placeholder="Message" value={body} onChange={e => setBody(e.target.value)} />
            <button data-testid={T.inbox.send} disabled={sending} onClick={send} className="owc-btn-accent text-sm inline-flex items-center gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
            </button>
          </div>
        </div>

        <div className="owc-card p-5 max-h-[75vh] overflow-y-auto">
          <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] mb-4">Recent messages</h2>
          {messages.length === 0 ? (
            <div className="text-slate-500 text-sm">No messages yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {messages.map(m => (
                <li key={m.id} data-testid={T.inbox.row(m.id)} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase text-slate-500">{m.channel} · {m.direction}</div>
                    <div className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-1 text-sm font-semibold text-[color:var(--owc-navy)]">{contactName(m.contact_id)}</div>
                  {m.subject && <div className="text-sm text-slate-700">{m.subject}</div>}
                  <div className="text-sm text-slate-600 mt-1">{m.body}</div>
                  <div className="mt-1"><span className={`owc-badge-${m.status === "sent" ? "green" : m.status === "stub" ? "slate" : m.status === "failed" ? "red" : "blue"}`}>{m.status}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
